import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { Layout } from "./components/Layout";
import { IdleTimeout } from "./components/IdleTimeout";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { LLCManagementPage } from "./pages/LLCManagementPage";
import { BankManagementPage } from "./pages/BankManagementPage";
import { PayeeManagementPage } from "./pages/PayeeManagementPage";
import { CreateCheckPage } from "./pages/CreateCheckPage";
import { CheckRegisterPage } from "./pages/CheckRegisterPage";
import { PaymentRegisterPage } from "./pages/PaymentRegisterPage";
import { WireInstructionsPage } from "./pages/WireInstructionsPage";
import ACHTransfersPage from "./pages/ACHTransfersPage";
import { TaxCompliancePage } from "./pages/TaxCompliancePage";
import { UserManagementPage } from "./pages/UserManagementPage";
import { SettingsPage } from "./pages/SettingsPage";
import { LearnMorePage } from "./pages/LearnMorePage";
import { useEffect, useState } from "react";

const MobileGate = ({ children }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  if (isMobile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 px-8 text-center">
        <img src="/logo.png" alt="PayMasterGeneral" className="w-20 h-20 rounded-2xl mb-6 shadow-lg" />
        <h1 className="text-xl font-bold text-white mb-2">PayMasterGeneral</h1>
        <p className="text-slate-400 text-sm leading-relaxed max-w-xs">
          This application is designed for desktop use. Please log in from a desktop or laptop computer for the best experience.
        </p>
      </div>
    );
  }

  return children;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (user?.role === 'clerk') return <Navigate to="/" replace />;
  return <ProtectedRoute>{children}</ProtectedRoute>;
};

const PageTracker = () => {
  const location = useLocation();
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/pageview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page: location.pathname }),
    }).catch(() => {});
  }, [location.pathname]);
  return null;
};

const AppRoutes = () => {
  return (
    <>
    <PageTracker />
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/llcs"
        element={
          <AdminRoute>
            <LLCManagementPage />
          </AdminRoute>
        }
      />
      <Route
        path="/banks"
        element={
          <AdminRoute>
            <BankManagementPage />
          </AdminRoute>
        }
      />
      <Route
        path="/payees"
        element={
          <AdminRoute>
            <PayeeManagementPage />
          </AdminRoute>
        }
      />
      <Route
        path="/checks/create"
        element={
          <ProtectedRoute>
            <CreateCheckPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/checks/register"
        element={
          <ProtectedRoute>
            <CheckRegisterPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payments"
        element={
          <ProtectedRoute>
            <PaymentRegisterPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wires"
        element={
          <ProtectedRoute>
            <WireInstructionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ach"
        element={
          <ProtectedRoute>
            <ACHTransfersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tax-compliance"
        element={
          <AdminRoute>
            <TaxCompliancePage />
          </AdminRoute>
        }
      />
      <Route
        path="/users"
        element={
          <AdminRoute>
            <UserManagementPage />
          </AdminRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <AdminRoute>
            <SettingsPage />
          </AdminRoute>
        }
      />
      <Route path="/learn-more" element={<LearnMorePage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
};

function App() {
  return (
    <MobileGate>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <IdleTimeout />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </MobileGate>
  );
}

export default App;
